import type { MouseEvent } from 'react';
import type { IPartnersContact } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';

/**
 * Phase 7c — one contact row as emitted by `addContact`'s RAW HTML STRING
 * (`partners.js:212-242`), i.e. the row you get immediately after clicking ADD.
 * Discovery §4.5 / **Q2**.
 *
 * ★ THERE ARE NOW THREE CONTACT-ROW SHAPES ON THIS PAGE, ALL DIFFERENT:
 * `ContactRow` (the Handlebars template), `ContactSearchRow` (`contactRender`'s
 * string) and this one (`addContact`'s string). Legacy never re-renders after a
 * create — `addContact` does `content.append(...)` into
 * `.contact-content.contact_<id> .col-xs-12` (`:210`, the one `.col-xs-12`
 * inside the pane: `.scrollable-area.contact_fields_block`) — so this markup
 * stays on screen until the next `render()` or a reload, at which point the row
 * silently becomes a template row.
 *
 * Differences from `tmpl/partners.tmpl:222-262` (Q2), all mirrored below:
 *
 *  | | template row | added row (`partners.js:216-229`) |
 *  |---|---|---|
 *  | email input | `type="email"`, `class="contact_field press_english"` | `type="text"`, `class="contact_field"` (:222) |
 *  | mobile input | `class="contact_field phone-number-input"` | `class="contact_field"` (:227) |
 *  | values | `{{name}}` / `{{email}}` / `{{tel_mobile}}` | hardcoded `value=""` |
 *
 * Everything else IS the template shape — unlike `ContactSearchRow`, this
 * string keeps `autocomplete="new-password"`, the `.col-xs-4.row.margin-0`
 * name cell with its `icon-perm-identity-24px` and `.col-xs-11.ml_35` offset,
 * the `data-name="company_<co>_<ct>"` on both the row and the delete anchor,
 * and the `remove contact_delete` class ORDER.
 *
 * Consequences, none of which are to be "fixed":
 *  - `keypress .press_english` (`partners.js:45`) and `keypress
 *    .phone-number-input` (`:47`) do NOT apply to a contact you just added, so
 *    Cyrillic in Email and letters in Mobile are typeable until a reload;
 *  - `type="text"` on Email removes the `checkValidity()` gate that
 *    `changeInputContactFieldName` (`:161`) leans on, so a just-added row PATCHes
 *    a malformed address that a template row would have reverted;
 *  - the row's own `blockPaste` (`paste .press_english`, `:46`) is likewise
 *    inert here.
 *
 * ★ `data-name` IS PRESENT (unlike the search row), so deleting a just-added
 * contact takes the NORMAL §4.8 path — Q9 does not apply to it.
 *
 * ★ WHITESPACE. `<input>`, `<i>` and `<span>` each sit on their own line in the
 * source string, so the parsed DOM carries a collapsing space text node in both
 * gaps — identical to the template row, reproduced with explicit `{' '}`.
 *
 * ★ `value=""` NOT `defaultValue={contact.name}`. The append happens in the
 * create's success callback, when every field really is `null`, and legacy
 * hardcodes the empty attribute. Using `defaultValue` off the model is the same
 * string TODAY, and it keeps the row honest if a future refetch fills the model
 * in without a re-render — but the row is only ever mounted for a brand-new
 * contact, so both spellings are identical on the wire. Kept as the model read
 * for consistency with the other two row components.
 */
interface AddedContactRowProps {
  contact: IPartnersContact;
  /** `self.company_id` — `e.currentTarget.dataset.id`, i.e. a STRING (`:204`).
   * It is interpolated straight into `data-name`, so the attribute is byte-wise
   * the same as the template's `company_{{../id}}_{{id}}`. */
  companyId: string;
  /** Bootstrap's `.open` on the kebab's parent div — owned by `ContactList`. */
  open: boolean;
  onToggleMenu: (e: MouseEvent<HTMLButtonElement>, contactId: number) => void;
  /** ★ NOT A LEGACY PROP — see `ContactRow`. In practice always false here: you
   * cannot append a row to a pane whose ADD button is disabled. Threaded anyway
   * so the three row shapes stay interchangeable. */
  readOnly?: boolean;
}

/** `value=""` in the string; the model's fields are all `null` at append time. */
const v = (value: string | null | undefined) => value ?? '';

const AddedContactRow = ({
  contact,
  companyId,
  open,
  onToggleMenu,
  readOnly = false,
}: AddedContactRowProps) => {
  const dataName = `company_${companyId}_${contact.id}`;

  return (
    <div className="row every-row" data-id={contact.id} data-name={dataName}>
      <div className="col-xs-4 row margin-0">
        <div className="col-xs-1">
          <span className="icon-perm-identity-24px" />
        </div>
        <div className="col-xs-11 ml_35">
          <input
            autoComplete="new-password"
            className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
            type="text"
            name="name"
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
            defaultValue={v(contact.name)}
            placeholder="Name"
            id="contact_info_"
          />{' '}
          <i className="text-clear icon-close-1" />{' '}
          <span className="placeholder-label placeholder-name">Name</span>
        </div>
      </div>
      <div className="col-xs-4">
        {/* :222 — `type="text"` and NO `press_english` (Q2). */}
        <input
          autoComplete="new-password"
          className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
          type="text"
          name="email"
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
          defaultValue={v(contact.email)}
          placeholder="Email"
        />{' '}
        <i className="text-clear icon-close-1" />{' '}
        <span className="placeholder-label placeholder-email">Email</span>
      </div>
      <div className="col-xs-3">
        {/* :227 — NO `phone-number-input` (Q2). */}
        <input
          autoComplete="new-password"
          className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
          type="text"
          name="tel_mobile"
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
          defaultValue={v(contact.tel_mobile)}
          placeholder="Mobile"
        />{' '}
        <i className="text-clear icon-close-1" />{' '}
        <span className="placeholder-label placeholder-mobile">Mobile</span>
      </div>
      {/* ★ NOT LEGACY — the kebab is not rendered at all when the owning company
          is not this user's, so it cannot appear on hover. Its only item is
          Delete, which is exactly what ownership forbids. */}
      {/* The double space in `class="dropdown  dropdown_contact col-xs-1"` is in
          the source string too (:232). */}
      {!readOnly && (
        <div className="dropdown  dropdown_contact col-xs-1">
          <div className={open ? 'open' : ''}>
            <button
              className="btn btn-contact-delete dropdown-toggle icon-more-actions"
              type="button"
              data-toggle="dropdown"
              onClick={(e) => onToggleMenu(e, contact.id)}
            />
            <ul className="dropdown-menu">
              <li>
                {/* :238 — `remove contact_delete` WITH `data-name`, exactly like
                    the template row. */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  tabIndex={-1}
                  href="#"
                  data-id={contact.id}
                  className="remove contact_delete"
                  data-name={dataName}
                >
                  <i className="icon-trash" />
                  Delete
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddedContactRow;
